export class ArrayUtils {

    // ===============================
    // 1. VALIDATION & BASIC OPERATIONS
    // ===============================

    static isEmpty<T>(array: T[] | undefined): boolean {
        return array == undefined || array.length === 0;
    }

    static isNotEmpty<T>(array: T[] | undefined): boolean {
        return !this.isEmpty(array);
    }

    static empty<T>(array: T[]): T[] {
        array.splice(0, array.length);
        return array;
    }

    static getLastValue<T>(array: T[]): T | null {
        if (array.length === 0) {
            return null;
        }
        return array[array.length - 1];
    }

    static each<T>(array: T[], callback: (index: number, item: T) => void): void {
        for (let i = 0; i < array.length; i++) {
            callback(i, array[i]);
        }
    }

    // ===============================
    // 2. SEARCH & FIND OPERATIONS
    // ===============================

    static contains(array: string[], partial: string, strict: boolean = false): boolean {
        for (let i = 0; i < array.length; i++) {
            if (!strict && array[i].includes(partial)) {
                return true;
            }
            if (strict && array[i] === partial) {
                return true;
            }
        }
        return false;
    }

    static indexOfPartial(array: string[], partial: string): number {
        for (let i = 0; i < array.length; i++) {
            if (array[i].includes(partial)) {
                return i;
            }
        }
        return -1;
    }

    static firstOrDefault<T>(array: T[], predicate: (item: T) => boolean): T | null {
        for (let i = 0; i < array.length; i++) {
            if (predicate(array[i])) {
                return array[i];
            }
        }
        return null;
    }

    static findIndex<T>(array: T[], predicate: (item: T) => boolean): number {
        for (let i = 0; i < array.length; i++) {
            if (predicate(array[i])) {
                return i;
            }
        }
        return -1;
    }

    static where<T>(array: T[], predicate: (item: T) => boolean): T[] {
        return array.filter(predicate);
    }

    // ===============================
    // 3. TRANSFORMATION OPERATIONS
    // ===============================

    static toObjectArray<T>(array: T[], objName: string): any[] {
        if (objName === undefined || objName === null) {
            throw new Error("Property name must be provided for conversion.");
        }

        const items = [...array];
        if (
            typeof items[0] === "string" ||
            typeof items[0] === "number" ||
            typeof items[0] === "boolean"
        ) {
            return items.map(val => {
                const obj = {};
                obj[objName] = val;
                return obj;
            });
        } else {
            return items;
        }
    }

    static getValuesByKey(array: any[], key: string): any[] {
        return array.map(element => element[key]) ?? [];
    }

    static distinct<T>(array: T[]): T[] {
        return array.filter((value, index, self) => self.indexOf(value) === index);
    }

    // ===============================
    // 4. GROUPING & AGGREGATION OPERATIONS
    // ===============================

    static groupBy(array: any[], groupKey: string, sumKey: string): any[] {
        const result: any[] = [];

        array.reduce((res, value) => {
            if (!res[value[groupKey]]) {
                res[value[groupKey]] = { key: value[groupKey], sumValue: 0 };
                result.push(res[value[groupKey]]);
            }
            res[value[groupKey]].sumValue += value[sumKey] || 0;
            return res;
        }, {});

        return result;
    }

    static groupBySumMultiple(array: any[], groupKey: string, sumKeys: string[]): any[] {
        const result: any[] = [];

        array.reduce((res, value) => {
            if (!res[value[groupKey]]) {
                res[value[groupKey]] = { key: value[groupKey], count: 0 };
                sumKeys.forEach(k => res[value[groupKey]][k] = 0);
                result.push(res[value[groupKey]]);
            }

            res[value[groupKey]].count++;
            sumKeys.forEach(k => res[value[groupKey]][k] += value[k] || 0);

            return res;
        }, {});

        return result;
    }

    static groupByKeysSumMultiple(array: any[], groupKeys: string[], sumKeys: string[]): any[] {
        const result: any[] = [];

        array.reduce((res, value) => {
            const key = groupKeys.map(k => value[k]).join('|');

            if (!res[key]) {
                res[key] = { key: key, count: 0 };
                groupKeys.forEach(k => res[key][k] = value[k]);
                sumKeys.forEach(k => res[key][k] = 0);
                result.push(res[key]);
            }

            res[key].count++;
            sumKeys.forEach(k => res[key][k] += value[k] || 0);

            return res;
        }, {});

        return result;
    }

    static groupAndSum(array: any[], groupKeys: string[], sumKeys: string[]): any[] {
        const hash = Object.create(null);
        const grouped: any[] = [];

        array.forEach(o => {
            const key = groupKeys.map(k => o[k]).join('|');
            if (!hash[key]) {
                hash[key] = Object.keys(o).reduce((result, key) => {
                    result[key] = o[key];
                    if (sumKeys.includes(key)) {
                        result[key] = 0;
                    }
                    return result;
                }, {});
                grouped.push(hash[key]);
            }
            sumKeys.forEach(k => hash[key][k] += o[k] || 0);
        });

        return grouped;
    }

    static sumBy<T>(array: T[], selector: (x: T) => number): number {
        return array.reduce((pre, cur) => pre + selector(cur), 0);
    }

    static sumByField(array: any[], field: string): number {
        return array.reduce((sum, item) => sum + (item[field] || 0), 0);
    }

    // ===============================
    // 5. SORTING OPERATIONS
    // ===============================

    static sortBy<T>(array: T[], field: string): T[] {
        return [...array].sort((a, b) => (a[field] > b[field]) ? 1 : (a[field] < b[field]) ? -1 : 0);
    }

    static sortByDesc<T>(array: T[], field: string): T[] {
        return [...array].sort((b, a) => (a[field] > b[field]) ? 1 : (a[field] < b[field]) ? -1 : 0);
    }

    static sortByValues<T>(array: T[], field: string, values: any[]): T[] {
        return [...array].sort((a, b) => {
            const aIndex = values.indexOf(a[field]);
            const bIndex = values.indexOf(b[field]);

            // Eğer her ikisi de values'da varsa, sıralama yap
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }

            // Eğer sadece a values'da varsa, a önce gelsin
            if (aIndex !== -1 && bIndex === -1) {
                return -1;
            }

            // Eğer sadece b values'da varsa, b önce gelsin
            if (aIndex === -1 && bIndex !== -1) {
                return 1;
            }

            // Her ikisi de values'da yoksa, orijinal sırayı koru
            return 0;
        });
    }

    static sortByMultiple<T>(array: T[], fields: string[]): T[] {
        return [...array].sort((a, b) => {
            for (let i = 0; i < fields.length; i++) {
                const field = fields[i];

                if (a[field] > b[field]) {
                    return 1;
                }
                if (a[field] < b[field]) {
                    return -1;
                }
            }
            return 0;
        });
    }

    // ===============================
    // 6. MODIFICATION OPERATIONS
    // ===============================

    static remove<T>(array: T[], item: T): boolean {
        const index = array.indexOf(item);
        if (index >= 0) {
            array.splice(index, 1);
            return true;
        }
        return false;
    }

    static replace<T>(array: T[], item: T, newItem: T): boolean {
        const index = array.indexOf(item);
        if (index >= 0) {
            array[index] = newItem;
            return true;
        }
        return false;
    }

    static pushRange<T>(array: T[], items: T[]): void {
        array.push(...items);
    }

    static removeRange<T>(array: T[], items: T[]): void {
        items.forEach(item => this.remove(array, item));
    }
} 